<script>
import {ElMessage, ElMessageBox, ElNotification} from "element-plus";
import {CopyDocument, RefreshLeft, Setting, CircleClose} from "@element-plus/icons-vue";
import MfEmpty from "@/components/core/MfEmpty.vue";
import {blobErrorMessage} from "@/utils/common";
import {
  boardTypeOptions,
  boardTypeLabels,
  boardStageOptions,
  boardStageLabels,
  boardStageTagTypes,
} from "./boardTypes";

const FAILED_PARTNERSHIP_TITLE =
    "Sheriklik asosida qatnashgan tanaffusdagi mahallalarning kerakli mablag'ni " +
    "to'play olmagan tashabbuslarini bekor qilish";

export default {
  name: "BoardList",
  components: {MfEmpty, CopyDocument, RefreshLeft, Setting, CircleClose},
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: "",
      dataList: [],
      restoringId: null,
      deletingId: null,
      failedPartnershipTitle: FAILED_PARTNERSHIP_TITLE,
      fpDialogVisible: false,
      fpRow: null,
      fpPreviewLoading: false,
      fpCommitLoading: false,
      filters: {
        search: "",
        stage: "",
        year: "",
        season: "",
        status: "",
      },
      boardTypeOptions,
      boardTypeLabels,
      boardStageOptions,
      boardStageLabels,
      boardStageTagTypes,
      statusOptions: [
        {value: "", label: "Faol mavsumlar"},
        {value: 1, label: "Barcha mavsumlar"},
        {value: 2, label: "Nofaol mavsumlar"},
      ],
    };
  },
  computed: {
    // Endpoint serverda ham admin-only — non-adminlarga tugmani ko'rsatmaymiz.
    isAdmin() {
      return this.$store.state.role === "admin";
    },
  },
  methods: {
    buildParams() {
      const params = {};
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.stage) params.stage = this.filters.stage;
      if (this.filters.year) params.year = this.filters.year;
      if (this.filters.season) params.season = this.filters.season;
      if (this.filters.status !== "") params.status = this.filters.status;
      return params;
    },
    fetchBoards() {
      this.loading = true;
      this.hasError = false;
      this.errorMessage = "";
      this.$http
          .get("api/v1/boards-filtered", {params: this.buildParams()})
          .then(({data}) => {
            if (Array.isArray(data)) {
              this.dataList = data;
            } else {
              this.dataList = data?.boards || data?.items || [];
            }
          })
          .catch(({response}) => {
            this.hasError = true;
            this.errorMessage =
                response?.data?.message ||
                "Mavsumlar ro'yxatini yuklashda xatolik yuz berdi.";
            ElNotification.error({
              title: "Xatolik",
              message: this.errorMessage,
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    onSearch() {
      this.fetchBoards();
    },
    resetFilters() {
      this.filters = {search: "", stage: "", year: "", season: "", status: ""};
      this.fetchBoards();
    },
    stageLabel(stage) {
      if (!stage) return "Noma'lum";
      return this.boardStageLabels[stage] || stage;
    },
    stageTagType(stage) {
      if (!stage) return "info";
      return this.boardStageTagTypes[stage] || "info";
    },
    typeLabel(type) {
      return this.boardTypeLabels[type] || type;
    },
    formatDate(value) {
      if (!value) return "-";
      return this.$moment(value).format("DD.MM.YYYY HH:mm");
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "-";
      return `${Number(value).toLocaleString("ru-RU")} so'm`;
    },
    onClickDelete(row) {
      ElMessageBox.confirm(
          "Bu mavsum ro'yxatlardan yashiriladi, keyin qayta tiklash mumkin. Davom etasizmi?",
          "Mavsumni faolsizlantirish",
          {
            confirmButtonText: "Ha, yashirish",
            cancelButtonText: "Bekor qilish",
            type: "warning",
          }
      )
          .then(() => {
            this.deletingId = row.id;
            this.$http
                .delete(`api/v1/boards/${row.id}`)
                .then(() => {
                  ElMessage({type: "success", message: "Mavsum muvaffaqiyatli yashirildi"});
                  this.fetchBoards();
                })
                .catch(({response}) => {
                  ElNotification.error({
                    title: "Xatolik",
                    message: response?.data?.message || "Iltimos, qayta urinib ko'ring.",
                  });
                })
                .finally(() => {
                  this.deletingId = null;
                });
          })
          .catch(() => {
            ElMessage({type: "info", message: "Amaliyot bekor qilindi"});
          });
    },
    onClickRestore(row) {
      ElMessageBox.confirm(
          "Bu mavsum qayta faollashtiriladi va ro'yxatlarda ko'rinadigan bo'ladi. Davom etasizmi?",
          "Mavsumni tiklash",
          {
            confirmButtonText: "Ha, tiklash",
            cancelButtonText: "Bekor qilish",
            type: "info",
          }
      )
          .then(() => {
            this.restoringId = row.id;
            this.$http
                .put(`api/v1/boards/${row.id}`, {is_active: true})
                .then(() => {
                  ElMessage({type: "success", message: "Mavsum muvaffaqiyatli tiklandi"});
                  this.fetchBoards();
                })
                .catch(({response}) => {
                  ElNotification.error({
                    title: "Xatolik",
                    message: response?.data?.message || "Iltimos, qayta urinib ko'ring.",
                  });
                })
                .finally(() => {
                  this.restoringId = null;
                });
          })
          .catch(() => {
            ElMessage({type: "info", message: "Amaliyot bekor qilindi"});
          });
    },
    // Endpoint partnership_end_date o'tmagan bo'lsa 400 qaytaradi — shu holatda
    // tugmani umuman ko'rsatmaymiz.
    canCancelPartnership(row) {
      if (!row.partnership_end_date) return false;
      return this.$moment(row.partnership_end_date).isBefore(this.$moment());
    },
    openFailedPartnership(row) {
      this.fpRow = row;
      this.fpDialogVisible = true;
    },
    getFileName(headers, fallback) {
      const contentDisposition =
          headers?.["content-disposition"] || headers?.["Content-Disposition"];
      if (!contentDisposition) return fallback;
      const part = contentDisposition
          .split(";")
          .find((item) => item.trim().startsWith("filename="));
      if (!part) return fallback;
      return decodeURIComponent(part.split("=")[1].trim().replace(/^"(.*)"$/, "$1"));
    },
    // save_stages=false — faqat hisobot (dry-run); true — tashabbuslarni haqiqatdan bekor qiladi.
    // Ikkala holatda ham javob .xlsx blob bo'lib keladi.
    requestFailedPartnership(saveStages) {
      const loadingKey = saveStages ? "fpCommitLoading" : "fpPreviewLoading";
      this[loadingKey] = true;
      // board_id va save_stages JSON body da yuborilishi shart — backend ShouldBind
      // Content-Type: application/json bo'lganda query paramlarni umuman o'qimaydi.
      return this.$http
          .post(
              "api/v1/failed-partnership",
              {board_id: this.fpRow.id, save_stages: saveStages},
              {responseType: "blob"}
          )
          .then((res) => {
            const url = URL.createObjectURL(
                new Blob([res.data], {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
                })
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                this.getFileName(res.headers, "failed-partnership.xlsx")
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return true;
          })
          .catch(async ({response}) => {
            // 400 xabarlari serverdan tayyor o'zbekcha matn — o'zgartirmasdan ko'rsatamiz.
            ElNotification.error({
              title: "Xatolik",
              message: await blobErrorMessage(
                  response,
                  "So'rovni bajarishda xatolik yuz berdi."
              ),
              duration: 6000,
            });
            return false;
          })
          .finally(() => {
            this[loadingKey] = false;
          });
    },
    onFpPreview() {
      this.requestFailedPartnership(false).then((ok) => {
        if (!ok) return;
        ElMessage({
          type: "success",
          message: "Ro'yxat yuklab olindi. Tekshirib chiqing, hech nima o'zgartirilmadi.",
        });
      });
    },
    onFpCommit() {
      ElMessageBox.confirm(
          "Ro'yxatdagi barcha tashabbuslar bekor qilinadi (holati \"o'tmadi\" ga o'zgaradi). " +
          "Bu amalni ortga qaytarib bo'lmaydi. Davom etasizmi?",
          "Tashabbuslarni bekor qilish",
          {
            confirmButtonText: "Ha, bekor qilinsin",
            cancelButtonText: "Yo'q",
            type: "warning",
            confirmButtonClass: "el-button--danger",
          }
      )
          .then(() => {
            this.requestFailedPartnership(true).then((ok) => {
              if (!ok) return;
              ElMessage({
                type: "success",
                message: "Tashabbuslar bekor qilindi va hisobot yuklab olindi.",
              });
              this.fpDialogVisible = false;
              this.fetchBoards();
            });
          })
          .catch(() => {
            ElMessage({type: "info", message: "Amaliyot bekor qilindi"});
          });
    },
  },
  mounted() {
    this.fetchBoards();
  },
};
</script>

<template>
  <div class="board-list">
    <div class="header">
      <div>
        <h2 class="page-title">Mavsumlar</h2>
        <p class="page-subtitle">
          Tashabbuslar, ovozlar va byudjet shu mavsumga (board) bog'lanadi
        </p>
      </div>
      <router-link to="/app/boards/add" class="el-button el-button--primary">
        Mavsum qo'shish
      </router-link>
    </div>

    <div class="filters">
      <el-input
          v-model="filters.search"
          placeholder="Nomi bo'yicha qidirish"
          clearable
          style="max-width: 260px"
          @keyup.enter="onSearch"
          @clear="onSearch"
      />
      <el-select
          v-model="filters.stage"
          placeholder="Bosqich"
          clearable
          style="width: 180px"
          @change="onSearch"
      >
        <el-option
            v-for="item in boardStageOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
        />
      </el-select>
      <el-input
          v-model="filters.year"
          placeholder="Yil"
          clearable
          style="width: 120px"
          @keyup.enter="onSearch"
          @clear="onSearch"
      />
      <el-input
          v-model="filters.season"
          placeholder="Mavsum raqami"
          clearable
          style="width: 150px"
          @keyup.enter="onSearch"
          @clear="onSearch"
      />
      <el-select v-model="filters.status" style="width: 200px" @change="onSearch">
        <el-option
            v-for="item in statusOptions"
            :key="item.label"
            :label="item.label"
            :value="item.value"
        />
      </el-select>
      <el-button type="primary" @click="onSearch">Qidirish</el-button>
      <el-button @click="resetFilters">Tozalash</el-button>
    </div>

    <div v-if="hasError" class="state-block">
      <el-result icon="error" title="Ma'lumotlarni yuklab bo'lmadi" :sub-title="errorMessage">
        <template #extra>
          <el-button type="primary" @click="fetchBoards">Qayta urinish</el-button>
        </template>
      </el-result>
    </div>

    <div v-else-if="loading" class="state-block">
      <el-skeleton :rows="8" animated/>
    </div>

    <div v-else-if="!dataList.length" class="state-block">
      <MfEmpty
          title="Mavsumlar topilmadi"
          description="Filtrlarni o'zgartirib ko'ring yoki yangi mavsum qo'shing"
      />
    </div>

    <el-table v-else :data="dataList" class="table" border>
      <el-table-column type="index" width="60" label="#"/>
      <el-table-column prop="title" label="Nomi" min-width="220">
        <template #default="scope">
          <span :class="{inactive: scope.row.is_active === false}">{{ scope.row.title }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Turi" min-width="150">
        <template #default="scope">
          <el-tag type="info" effect="plain">{{ typeLabel(scope.row.type) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="year" label="Yil" width="90"/>
      <el-table-column prop="season" label="Mavsum" width="100"/>
      <el-table-column label="Bosqich" min-width="150">
        <template #default="scope">
          <el-tag :type="stageTagType(scope.row.stage)" effect="dark" round>
            {{ stageLabel(scope.row.stage) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Holati" width="130">
        <template #default="scope">
          <el-tag v-if="scope.row.is_active === false" type="info" effect="plain">
            Nofaol
          </el-tag>
          <el-tag v-else type="success" effect="plain">Faol</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Umumiy summa" min-width="160">
        <template #default="scope">{{ formatMoney(scope.row.total_amount) }}</template>
      </el-table-column>
      <el-table-column prop="initiatives_count" label="Tashabbuslar soni" min-width="150"/>
      <el-table-column label="Qabul oynasi" min-width="180">
        <template #default="scope">
          {{ formatDate(scope.row.accept_start_date) }} — {{ formatDate(scope.row.accept_end_date) }}
        </template>
      </el-table-column>
      <el-table-column label="Moderator ruxsat muddati" min-width="180">
        <template #default="scope">
          <template v-if="scope.row.edit_permission_end_date">
            <div>{{ formatDate(scope.row.edit_permission_end_date) }}</div>
            <el-tag
                v-if="scope.row.can_grant_edit_permission === false"
                type="danger"
                effect="plain"
                size="small"
            >
              Muddat tugagan
            </el-tag>
          </template>
          <span v-else class="muted-cell">Cheklanmagan</span>
        </template>
      </el-table-column>
      <el-table-column fixed="right" label="Amallar" width="280" align="center">
        <template #default="scope">
          <el-space>
            <el-tooltip content="Tahrirlash" placement="top">
              <el-button
                  type="primary"
                  class="action-btn"
                  @click="$router.push({name: 'BoardsFormEdit', params: {id: scope.row.id}})"
              >
                <img height="16" src="@/assets/images/icons/edit.svg" alt="Tahrirlash"/>
              </el-button>
            </el-tooltip>
            <el-tooltip content="Nusxalash" placement="top">
              <el-button
                  class="action-btn"
                  @click="$router.push({name: 'BoardsFormAdd', query: {duplicate_id: scope.row.id}})"
              >
                <el-icon :size="16"><CopyDocument/></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip v-if="scope.row.is_active === false" content="Tiklash" placement="top">
              <el-button
                  type="success"
                  class="action-btn"
                  :loading="restoringId === scope.row.id"
                  @click="onClickRestore(scope.row)"
              >
                <el-icon :size="16"><RefreshLeft/></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip v-else content="Yashirish" placement="top">
              <el-button
                  type="danger"
                  class="action-btn"
                  :loading="deletingId === scope.row.id"
                  @click="onClickDelete(scope.row)"
              >
                <img height="16" src="@/assets/images/icons/delete.svg" alt="O'chirish"/>
              </el-button>
            </el-tooltip>
            <el-tooltip content="Ovoz berish sozlamasi" placement="top">
              <el-button
                  class="action-btn"
                  @click="$router.push({name: 'BoardsVoteSettings', params: {id: scope.row.id}})"
              >
                <el-icon :size="16"><Setting/></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip
                v-if="isAdmin && canCancelPartnership(scope.row) && scope.row.partnership_budget !== 0"
                :content="failedPartnershipTitle"
                placement="top"
                popper-class="fp-tooltip"
            >
              <el-button
                  type="warning"
                  class="action-btn"
                  :disabled="scope.row.stage === 'FINAL'"
                  @click="openFailedPartnership(scope.row)"
              >
                <el-icon :size="16"><CircleClose/></el-icon>
              </el-button>
            </el-tooltip>
          </el-space>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
        v-model="fpDialogVisible"
        title="Tashabbuslarni bekor qilish"
        width="620px"
        :close-on-click-modal="false"
    >
      <p class="fp-title">{{ failedPartnershipTitle }}</p>
      <p v-if="fpRow" class="fp-board">
        Mavsum: <strong>{{ fpRow.title }}</strong>
      </p>

      <el-alert type="warning" :closable="false" show-icon class="fp-alert">
        Avval ro'yxatni yuklab olib tekshiring — bu bosqichda hech nima o'zgarmaydi.
        Bekor qilish esa qaytarib bo'lmaydigan amal.
      </el-alert>

      <template #footer>
        <el-button
            type="primary"
            :loading="fpPreviewLoading"
            @click="onFpPreview"
        >
          Ro'yxatni yuklab olish (tekshirish)
        </el-button>
        <el-button
            type="danger"
            :loading="fpCommitLoading"
            @click="onFpCommit"
        >
          Tashabbuslarni bekor qilish
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;

  .page-title {
    margin: 0;
    font-family: "Montserrat-Bold", sans-serif;
    color: #0a194e;
  }

  .page-subtitle {
    margin: 4px 0 0;
    color: #777777;
    font-size: 13px;
  }
}

.el-button--primary {
  text-decoration: none;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.state-block {
  padding: 24px 0;
}

.inactive {
  color: #999;
  text-decoration: line-through;
}

.fp-title {
  margin: 0 0 8px;
  font-size: 15px;
  line-height: 1.5;
  color: #0a194e;
}

.fp-board {
  margin: 0 0 14px;
  color: #777777;
  font-size: 13px;
}

.fp-alert {
  margin-bottom: 4px;
}

.action-btn {
  width: 34px;
  height: 34px;
  padding: 0;
}

.muted-cell {
  color: #999;
}
</style>

<!-- Tooltip popper body ga teleport qilinadi — scoped style unga yetib bormaydi. -->
<style lang="scss">
.fp-tooltip.el-popper {
  max-width: 260px;
  line-height: 1.5;
}
</style>
